/* @flow */

import type { Point } from './Common';
import GlobalManager from './GlobalManager';
import Walker from './Walker';
import Goal from './Goal';
import FloatingRamp from './FloatingRamp';
import Platform from './Platform';
import Umbrella from './Umbrella';
import ParticleHose from './ParticleHose';
import FloorSurface from './FloorSurface';

export type GoalConfig = {
  position: Point,
  acceptedColors: Array<string>,
  tracks: Array<string>,
};

export type UmbrellaConfig = {
  bottomCenter: Point,
  radians: number,
  rampHypotenuse: number,
};

export type FloatingRampConfig = {
  bottomCenter: Point,
  radians: number,
  hypotenuse: number,
};

export type PlatformConfig = {
  centerPosition: Point,
  radians: number,
  hypotenuse: number,
};

export type ParticleHoseConfig = {
  origin: Point,
  color: string,
};

export type LevelConfig = {
  songKey: string,
  topMessage: Array<string>,
  autoplayTracks: Array<string>,
  goals: Array<GoalConfig>,
  umbrellas: Array<UmbrellaConfig>,
  floatingRamps: Array<FloatingRampConfig>,
  platforms: Array<PlatformConfig>,
  particleHoses: Array<ParticleHoseConfig>,
};

export default class Level {
  onComplete: () => void;
  completed = false;
  objects: *;

  constructor(onComplete: () => void, levelConfig: LevelConfig) {
    GlobalManager.cleanupLevel();
    GlobalManager.cleanupAudio();

    const self = this;
    this.onComplete = () => {
      if (!self.completed) {
        self.completed = true;
        onComplete();
      }
    };

    const floor = new FloorSurface();
    const goals = levelConfig.goals.map(g => new Goal(g.position, g.acceptedColors, g.tracks));
    const umbrellas = levelConfig.umbrellas.map(
      u => new Umbrella(u.bottomCenter, u.radians, u.rampHypotenuse)
    );
    const ramps = [
      ...umbrellas.reduce((map, u) => {
        return map.concat(u.ramps);
      }, []),
      ...levelConfig.floatingRamps.map(
        fr => new FloatingRamp(fr.bottomCenter, fr.radians, fr.hypotenuse)
      ),
    ];
    const surfaces = [
      floor,
      ...ramps,
      ...levelConfig.platforms.map(
        pl => new Platform(pl.centerPosition, pl.radians, pl.hypotenuse)
      ),
    ];
    const hoses = levelConfig.particleHoses.map(
      ph => new ParticleHose(goals, ramps, ph.origin, ph.color)
    );

    // todo pass songs in levelConfig
    GlobalManager.song.loadSong(levelConfig.songKey);
    GlobalManager.song.playTracks(levelConfig.autoplayTracks);
    const hero = new Walker(umbrellas, surfaces);

    this.objects = {
      goals,
      umbrellas,
      ramps,
      surfaces,
      hoses,
      hero,
      floor,
    };

    GlobalManager.setMessageTop(levelConfig.topMessage);
    GlobalManager.input.subscribeLevelKeyPress('Enter', () => this.onTryComplete());
    GlobalManager.clock.subscribeLevel(() => this.step());
  }

  checkComplete() {
    const { goals } = this.objects;
    return goals.every(g => g.isCleared());
  }
  onTryComplete() {
    if (this.checkComplete()) {
      this.onComplete();
    }
  }
  step() {
    const messages = [];

    const levelComplete = this.checkComplete();
    if (levelComplete) {
      messages.push('level complete, press [enter] to continue');
    }
    GlobalManager.setMessageCenter(messages);
  }
}



// WEBPACK FOOTER //
// ./src/Level.js